/**
 * memorial-days-engine.js — даты поминовения усопших по традиции Русской
 * Православной Церкви: личные дни (3-й, 9-й, 40-й, полгода, годовщины) с
 * переносом церковного поминовения, и общие дни года (родительские субботы,
 * Радоница, поминовение воинов).
 *
 * Чистая арифметика дат в UTC (дни — целые, летнего времени нет), без DOM.
 * Экспортирует `window.MemorialDays`; CommonJS — для node-тестов
 * (tests/js/memorial-days-engine.test.mjs — там же лежат контрольные даты
 * Пасхи и родительских суббот, сверенные с церковным календарём).
 *
 * Правила переноса — ТОЛЬКО те, что подтверждены источниками (см. тест и
 * переводы `transfer.*`); чего в правилах нет, движок молча не выдумывает.
 * Единица правды о дне смерти: `opts.from` — 'death' (день смерти — 1-й день,
 * умолчание) или 'next' (счёт со следующего дня). Часы смерти движок не
 * интерпретирует: правило выбирает человек, движок печатает базу.
 *
 * Традиции (`opts.tradition`, умолчание 'ru') различаются НАБОРОМ дней и
 * общими днями года; правила переноса — общее православное ядро с двумя
 * отличиями: куда уходит пасхальное окно (Радоница — русский обычай, у
 * остальных — суббота после Фоминой недели) и сербские особенности (40-й день
 * не переносят никогда, полгода и годовщину поминают в субботу ПЕРЕД датой).
 * Источники наборов — в tests/js/memorial-days-engine.test.mjs.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) { module.exports = api; }
    if (root) { root.MemorialDays = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
'use strict';

var DAY = 86400000;

function utc(y, m, d) { return new Date(Date.UTC(y, m, d)); }
function pad(n) { return (n < 10 ? '0' : '') + n; }
function iso(d) { return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate()); }
function parse(s) {
    if (s instanceof Date) return utc(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
    if (!m) return null;
    var d = utc(+m[1], +m[2] - 1, +m[3]);
    return (d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3]) ? d : null;
}
function addDays(d, n) { return new Date(d.getTime() + n * DAY); }
function diffDays(a, b) { return Math.round((b.getTime() - a.getTime()) / DAY); }
function dow(d) { return d.getUTCDay(); } // 0 = Sunday … 6 = Saturday
function daysInMonth(y, m) { return utc(y, m + 1, 0).getUTCDate(); }
/** Тот же день через n месяцев; 31-е → последнее число короткого месяца. */
function addMonthsClamped(d, n) {
    var y = d.getUTCFullYear(), m = d.getUTCMonth() + n, day = d.getUTCDate();
    var ty = y + Math.floor(m / 12), tm = ((m % 12) + 12) % 12;
    return utc(ty, tm, Math.min(day, daysInMonth(ty, tm)));
}

/* ── Православная Пасха (александрийская пасхалия, Meeus) → григорианская дата ── */
function pascha(year) {
    var a = year % 4, b = year % 7, c = year % 19;
    var d = (19 * c + 15) % 30;
    var e = (2 * a + 4 * b - d + 34) % 7;
    var month = Math.floor((d + e + 114) / 31);      // 3 = март, 4 = апрель (юлианские)
    var day = ((d + e + 114) % 31) + 1;
    var julian = utc(year, month - 1, day);
    var shift = Math.floor(year / 100) - Math.floor(year / 400) - 2; // юлианский → григорианский (13 дней в 1900–2099)
    return addDays(julian, shift);
}

/* ── Окна года, в которых церковное поминовение не совершается ── */
function windows(year) {
    var p = pascha(year);
    return {
        pascha: p,
        cleanMonday: addDays(p, -48),     // начало Великого поста
        lentWeek5End: addDays(p, -16),    // пятница 5-й седмицы — последняя суббота с панихидой впереди (P−15)
        lentWeekdaysEnd: addDays(p, -9),  // пятница 6-й седмицы — последний будний день поста
        lazarus: addDays(p, -8),          // Лазарева суббота
        palmSunday: addDays(p, -7),
        holyWeekStart: addDays(p, -6),
        brightWeekEnd: addDays(p, 6),
        radonitsa: addDays(p, 9),
        thomasSunday: addDays(p, 7),
        saturdayAfterThomas: addDays(p, 13),
        ascension: addDays(p, 39),
        trinitySaturday: addDays(p, 48),
        trinity: addDays(p, 49)
    };
}

/* Двунадесятые праздники с неподвижной датой (н. ст.) — в сам день заупокойных
   служб обычно нет; это ПОМЕТКА, а не перенос: единой даты «куда» у Устава нет. */
var FIXED_GREAT_FEASTS = { '01-19': 1, '02-15': 1, '04-07': 1, '08-19': 1, '08-28': 1, '09-21': 1, '09-27': 1, '12-04': 1 };
function mmdd(d) { return pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate()); }

/**
 * Куда переносится ЦЕРКОВНОЕ поминовение, если личный день попал в окно, где
 * заупокойные службы не совершаются. Возвращает null, когда переноса нет.
 *   'radonitsa'    — от Лазаревой субботы до Радоницы панихид нет → Радоница
 *                    (сюда же будни 6-й седмицы поста: их суббота — Лазарева).
 *   'lentSaturday' — будни (пн–пт) 1–5-й седмиц Великого поста → ближайшая суббота.
 *   'trinity'      — день Пятидесятницы → накануне, Троицкая родительская суббота.
 *   'nativity'     — Рождество Христово → 8 января (принято также 6-го, в сочельник).
 */
function transfer(date, tradition, itemKey) {
    var tr = TRADITIONS[tradition] || TRADITIONS.ru;
    var w = windows(date.getUTCFullYear());
    var paschal = tr.paschalTarget === 'radonitsa'
        ? { reason: 'radonitsa', date: w.radonitsa }
        : { reason: 'thomas', date: w.saturdayAfterThomas };
    var paschalEnd = tr.paschalTarget === 'radonitsa' ? w.radonitsa : addDays(w.thomasSunday, 1);
    if (tr.fixed && itemKey && tr.fixed.indexOf(itemKey) >= 0) return null;   // сербская четрдесетница — день в день
    if (date >= w.lazarus && date < paschalEnd) return paschal;
    if (date >= w.cleanMonday && date <= w.lentWeekdaysEnd) {
        var wd = dow(date);
        if (wd >= 1 && wd <= 5) {
            if (date > w.lentWeek5End) return paschal;
            return { reason: 'lentSaturday', date: addDays(date, 6 - wd) };
        }
    }
    if (date.getTime() === w.trinity.getTime()) return { reason: 'trinity', date: w.trinitySaturday };
    if (mmdd(date) === '01-07') return { reason: 'nativity', date: utc(date.getUTCFullYear(), 0, 8) };
    if (tr.saturdayBefore && itemKey && tr.saturdayBefore.indexOf(itemKey) >= 0 && dow(date) !== 6) {
        return { reason: 'saturdayBefore', date: addDays(date, -((dow(date) + 1) % 7)) };
    }
    return null;
}

/**
 * Пометка без даты переноса: день, когда заупокойная служба в храме обычно не
 * совершается, но единого правила «куда» нет — человеку нужно спросить в храме.
 *   'svyatki'    — Святки (8–18 января; узкое окно, есть и более широкое толкование).
 *   'greatFeast' — двунадесятый праздник (неподвижный или Вознесение).
 */
function note(date) {
    var md = mmdd(date);
    if (FIXED_GREAT_FEASTS[md]) return 'greatFeast';
    if (date.getTime() === windows(date.getUTCFullYear()).ascension.getTime()) return 'greatFeast';
    if (md >= '01-08' && md <= '01-18') return 'svyatki';
    return null;
}

/* ── Личные дни поминовения: каталог и наборы по традициям ── */
var DAY_DEFS = {
    day3:   { kind: 'day',   n: 3 },
    day7:   { kind: 'day',   n: 7 },
    day9:   { kind: 'day',   n: 9 },
    day20:  { kind: 'day',   n: 20 },
    day40:  { kind: 'day',   n: 40 },
    month3: { kind: 'month', n: 3 },
    half:   { kind: 'month', n: 6 },
    month9: { kind: 'month', n: 9 },
    year1:  { kind: 'year',  n: 1 },
    year2:  { kind: 'year',  n: 2 },
    year3:  { kind: 'year',  n: 3 },
    year7:  { kind: 'year',  n: 7 }
};

/* Наборы дней и общих дат по традициям. Общие ключи — те же формулы от Пасхи:
   meatfare P−57 · lent2/3/4 P−36/−29/−22 · radonitsa P+9 · trinity P+48 ·
   demetrius — суббота перед 8 ноября (у сербов Митровске, у болгар Архангелова,
   в румынском календаре Патриархии — Moșii de toamnă) · miholjske — суббота
   перед 12 октября (только Сербия) · may9 / sep11 — только РПЦ. */
var TRADITIONS = {
    ru:        { days: ['day3', 'day9', 'day40', 'half', 'year1', 'year2', 'year3'],
                 general: ['meatfare', 'lent2', 'lent3', 'lent4', 'radonitsa', 'may9', 'trinity', 'sep11', 'demetrius'],
                 paschalTarget: 'radonitsa' },
    greek:     { days: ['day3', 'day9', 'day40', 'month3', 'half', 'month9', 'year1', 'year3'],
                 general: ['meatfare', 'lent2', 'lent3', 'lent4', 'trinity'],
                 paschalTarget: 'thomas' },
    serbian:   { days: ['day3', 'day7', 'day40', 'half', 'year1'],
                 general: ['meatfare', 'lent2', 'lent3', 'lent4', 'trinity', 'miholjske', 'demetrius'],
                 paschalTarget: 'thomas', fixed: ['day40'], saturdayBefore: ['half', 'year1'] },
    romanian:  { days: ['day3', 'day9', 'day40', 'month3', 'half', 'month9', 'year1', 'year2', 'year3', 'year7'],
                 general: ['meatfare', 'lent2', 'lent3', 'lent4', 'trinity', 'demetrius'],
                 paschalTarget: 'thomas' },
    bulgarian: { days: ['day3', 'day9', 'day20', 'day40', 'month3', 'half', 'month9', 'year1'],
                 general: ['meatfare', 'lent2', 'lent3', 'lent4', 'trinity', 'demetrius'],
                 paschalTarget: 'thomas' }
};
function traditionOf(key) { return TRADITIONS[key] ? key : 'ru'; }

/**
 * @param deathIso 'YYYY-MM-DD'
 * @param opts {from: 'death'|'next', today: 'YYYY-MM-DD'|Date, tradition: 'ru'|'greek'|'serbian'|'romanian'|'bulgarian'}
 * @returns {base, items[], todayIndex, next} либо null при плохой дате
 */
function personal(deathIso, opts) {
    opts = opts || {};
    var death = parse(deathIso);
    if (!death) return null;
    var tradition = traditionOf(opts.tradition);
    var base = opts.from === 'next' ? addDays(death, 1) : death;
    var today = parse(opts.today) || parse(iso(new Date()));

    var items = TRADITIONS[tradition].days.map(function (key) {
        var def = DAY_DEFS[key];
        var date = def.kind === 'day' ? addDays(base, def.n - 1)
                 : def.kind === 'month' ? addMonthsClamped(base, def.n)
                 : addMonthsClamped(base, def.n * 12);
        var t = transfer(date, tradition, key);
        var inDays = diffDays(today, date);
        return {
            key: key, kind: def.kind, n: def.n,
            date: date, iso: iso(date),
            transfer: t ? { reason: t.reason, date: t.date, iso: iso(t.date) } : null,
            note: t ? null : note(date),
            inDays: inDays,
            status: inDays === 0 ? 'today' : inDays < 0 ? 'past' : 'future'
        };
    });

    var next = null;
    for (var i = 0; i < items.length; i++) {
        if (items[i].inDays >= 0) { next = items[i]; break; }
    }
    var todayIndex = diffDays(base, today) + 1; // 1 в день базы; ≤ 0 — до неё

    return {
        death: death, deathIso: iso(death),
        base: base, baseIso: iso(base),
        tradition: tradition,
        from: opts.from === 'next' ? 'next' : 'death',
        today: today, todayIso: iso(today),
        todayIndex: todayIndex,
        items: items,
        next: next
    };
}

/* ── Общие дни поминовения года ── */
function demetriusSaturday(year) {
    // Суббота ПЕРЕД 8 ноября (память вмч. Димитрия); если 8 ноября само суббота —
    // предыдущая (1 ноября). Совпадение с 4 ноября (Казанская икона) переносит
    // поминовение на субботу раньше — так было в 2023 году (28 октября).
    var nov8 = utc(year, 10, 8);
    var back = dow(nov8) === 6 ? 7 : (dow(nov8) + 1) % 7;
    var sat = addDays(nov8, -back);
    if (sat.getUTCMonth() === 10 && sat.getUTCDate() === 4) sat = addDays(sat, -7);
    return sat;
}

function saturdayBefore(year, month0, day) {
    var d = utc(year, month0, day);
    var back = dow(d) === 6 ? 7 : (dow(d) + 1) % 7;
    return addDays(d, -back);
}

function general(year, tradition) {
    tradition = traditionOf(tradition);
    var p = pascha(year);
    var w = windows(year);
    var meatfare = { key: 'meatfare', date: addDays(p, -57), movable: true, note: null };
    // Мясопустная суббота = Сретение (15 февраля; ближайший случай — 2031): по
    // Типикону заупокойная служба поётся в предыдущую субботу.
    if (mmdd(meatfare.date) === '02-15') { meatfare.date = addDays(meatfare.date, -7); meatfare.note = 'meatfareMeeting'; }

    function lentSat(key, offset) {
        var it = { key: key, date: addDays(p, offset), movable: true, note: null };
        // Полиелейный праздник в родительскую субботу ОТМЕНЯЕТ заупокойную службу
        // Триоди (указания Патриархии, 22.03.2025): 40 мучеников — 22 марта,
        // Благовещение — 7 апреля. Дата остаётся, служба — нет.
        var md = mmdd(it.date);
        if (md === '03-22') it.note = 'cancelled40';
        else if (md === '04-07') it.note = 'cancelledAnnunciation';
        return it;
    }

    var may9 = { key: 'may9', date: utc(year, 4, 9), movable: false, note: null };
    // 9 мая внутри Пасхи…Антипасхи (2024, 2027, 2032, 2040) → поминовение воинов
    // переносится на Радоницу.
    if (may9.date >= p && may9.date < w.radonitsa) { may9.date = w.radonitsa; may9.note = 'may9Radonitsa'; }

    var all = {
        meatfare:  meatfare,
        lent2:     lentSat('lent2', -36),
        lent3:     lentSat('lent3', -29),
        lent4:     lentSat('lent4', -22),
        radonitsa: { key: 'radonitsa', date: w.radonitsa, movable: true, note: null },
        may9:      may9,
        trinity:   { key: 'trinity',   date: w.trinitySaturday, movable: true, note: null },
        sep11:     { key: 'sep11',     date: utc(year, 8, 11), movable: false, note: null },
        miholjske: { key: 'miholjske', date: saturdayBefore(year, 9, 12), movable: false, note: null },
        demetrius: { key: 'demetrius', date: demetriusSaturday(year), movable: false, note: null }
    };
    var list = TRADITIONS[tradition].general.map(function (k) { return all[k]; });
    list.sort(function (a, b) { return a.date - b.date; });
    list.forEach(function (it) { it.iso = iso(it.date); });
    return { year: year, tradition: tradition, pascha: p, paschaIso: iso(p), items: list };
}

/** Ближайший (сегодня или позже) общий день поминовения. */
function nextGeneral(todayIso, tradition) {
    var today = parse(todayIso) || parse(iso(new Date()));
    var y = today.getUTCFullYear();
    for (var yy = y; yy <= y + 1; yy++) {
        var g = general(yy, tradition);
        for (var i = 0; i < g.items.length; i++) {
            if (g.items[i].date >= today) {
                var it = g.items[i];
                return { key: it.key, date: it.date, iso: it.iso, year: yy, inDays: diffDays(today, it.date) };
            }
        }
    }
    return null;
}

return {
    pascha: pascha,
    windows: windows,
    transfer: transfer,
    note: note,
    personal: personal,
    general: general,
    nextGeneral: nextGeneral,
    demetriusSaturday: demetriusSaturday,
    traditions: Object.keys(TRADITIONS),
    traditionOf: traditionOf,
    parse: parse,
    iso: iso,
    addDays: addDays,
    addMonthsClamped: addMonthsClamped,
    diffDays: diffDays
};
});
